import { OrderInAdvanceMode } from 'constants/enums';
import { DayKey, HourData } from 'components/restaurantInformation/components/types';
import { OrderInAdvanceSettings, TimeRange } from 'store/menuDetailsStore/reducer';

export type OrderInAdvanceSettingsValues = Omit<Partial<OrderInAdvanceSettings>, 'price'> & {
    price?: number | string | null;
};

const DAY_KEYS: DayKey[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

export function getSizeOrderInAdvanceEnabled(settings?: OrderInAdvanceSettingsValues | null) {
    return settings?.isEnabled ?? false;
}

export function getOrderInAdvanceMode(
    settings?: OrderInAdvanceSettingsValues | null
): OrderInAdvanceMode {
    if (!settings) {
        return OrderInAdvanceMode.SameDay;
    }
    return settings.mode ?? OrderInAdvanceMode.Custom;
}

export function getTodayPickupHoursCaption(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined,
    pickupRange: TimeRange
): string | null {
    const todaySlots = (workingHours?.[DAY_KEYS[new Date().getDay()]] ?? [])
        .map((slot) => ({
            from: parseWorkingHourTime(slot?.from),
            to: parseWorkingHourTime(slot?.to),
        }))
        .filter(
            (slot): slot is { from: number; to: number } => slot.from !== null && slot.to !== null
        );
    const isDifferentFromRange =
        todaySlots.length > 1 ||
        (todaySlots.length === 1 &&
            (todaySlots[0].from !== pickupRange.from || todaySlots[0].to !== pickupRange.to));
    if (!isDifferentFromRange) {
        return null;
    }
    return todaySlots
        .map((slot) => `${formatPickupTime(slot.from)} - ${formatPickupTime(slot.to)}`)
        .join(', ');
}

export function formatPickupTime(value: number): string {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function isPickupDateToday(pickupDate?: string | null): boolean {
    if (!pickupDate) {
        return false;
    }
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate()
    ).padStart(2, '0')}`;
    return pickupDate.slice(0, 10) === today;
}

export function parseWorkingHourTime(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
}

export function getTakeAwayPickupRange(
    workingHours?: Partial<Record<DayKey, HourData[]>> | null
): TimeRange {
    const slots = Object.keys(workingHours ?? {}).reduce<HourData[]>(
        (all, day) => all.concat(workingHours?.[day as DayKey] ?? []),
        []
    );
    return slots.reduce<TimeRange>(
        (range, slot) => {
            const from = parseWorkingHourTime(slot?.from);
            const to = parseWorkingHourTime(slot?.to);
            return {
                from:
                    from !== null && (range.from === null || from < range.from) ? from : range.from,
                to: to !== null && (range.to === null || to > range.to) ? to : range.to,
            };
        },
        { from: null, to: null }
    );
}
